import { useEffect, useMemo, useState } from 'react';
import { DatePicker, Flex, Select, TextInput, createForm, useToasts } from '@applyboard/crystal-ui';
import type { Program } from '~/pages/ProgramCatalog/data/usePrograms';
import styled from '@emotion/styled';
import { useNavigate } from 'react-router-dom';
import { IntakeManagementPageInitialProps } from '~/data';
import { usePrograms } from '~/pages/ProgramCatalog/data/usePrograms';
import { HttpError } from '~/lib/useRequest/errors';
import { useCreateIntake } from '../data';

type IntakeCreateFormFields = {
  programId: string;
  campusId: string;
  intakeTermId: string;
  firstDayOfClass: string;
  lastDayOfClass: string;
  applicationFee: string;
  tuition: string;
  acceptingAppsStartAt: string;
  acceptingAppsCloseAt: string;
  deferralDeadlineAt: string;
  refundDeadlineAt: string;
};

type IntakeCreateFormProps = Readonly<{
  initialProps: IntakeManagementPageInitialProps;
}>;

const intakeCreateForm = createForm<IntakeCreateFormFields>();

const minDate = new Date(1900, 0, 1).toISOString();
const maxDate = new Date(2100, 0, 1).toISOString();

function validateDates(values: IntakeCreateFormFields): string | null {
  const isFirstDayAfterLastDay =
    values.firstDayOfClass &&
    values.lastDayOfClass &&
    new Date(values.firstDayOfClass) > new Date(values.lastDayOfClass);

  if (isFirstDayAfterLastDay) {
    return 'First Day of Class cannot be after Last Day of Class';
  }

  const isStartAfterClose =
    values.acceptingAppsStartAt &&
    values.acceptingAppsCloseAt &&
    new Date(values.acceptingAppsStartAt) > new Date(values.acceptingAppsCloseAt);

  if (isStartAfterClose) {
    return 'Accepting Applications Start cannot be after Accepting Applications Close';
  }

  return null;
}

function validateApplicationFee(value: string): { fee: number; error: string | null } {
  const fee = Number.parseInt(value, 10);
  if (Number.isNaN(fee) || fee < 0) {
    return { fee: 0, error: 'Application Fee must be a non-negative number' };
  }
  return { fee, error: null };
}

function validateTuition(value: string): { tuition: number; error: string | null } {
  const tuition = Number.parseInt(value, 10);
  if (Number.isNaN(tuition) || tuition < 0) {
    return { tuition: 0, error: 'Tuition must be a non-negative number' };
  }
  return { tuition, error: null };
}

function getErrorMessage(error: unknown): string {
  const fallback = 'Failed to create intake';

  const extractDetail = (body: unknown) => {
    if (!body || typeof body !== 'object') return undefined;
    const b = body as {
      errors?: Array<{ detail?: string; title?: string }>;
      response?: { errors?: Array<{ detail?: string; title?: string }> };
    };
    return (
      b.errors?.[0]?.detail || b.errors?.[0]?.title || b.response?.errors?.[0]?.detail || b.response?.errors?.[0]?.title
    );
  };

  if (error instanceof HttpError) {
    const detail = extractDetail(error.body);
    if (detail) {
      const normalizedDetail = detail.toLowerCase();
      if (normalizedDetail.includes('intake already available')) {
        return 'An intake for this Program, Campus, and Term already exists.';
      }
      return detail;
    }
  }

  if (!(error instanceof Error)) {
    return fallback;
  }

  return error.message || fallback;
}

type TuitionFieldsProps = Readonly<{
  programsMap: Record<string, Program>;
  useFieldValues: typeof intakeCreateForm.useFieldValues;
  useSetFieldValues: typeof intakeCreateForm.useSetFieldValues;
  Field: typeof intakeCreateForm.Field;
  isCreatingIntake: boolean;
}>;

function TuitionFields({
  programsMap,
  useFieldValues,
  useSetFieldValues,
  Field,
  isCreatingIntake,
}: TuitionFieldsProps) {
  const { programId } = useFieldValues(['programId']);
  const setFieldValues = useSetFieldValues();
  const selectedProgram = programId ? programsMap[programId] : null;
  const [previousProgramId, setPreviousProgramId] = useState<string | null>(null);

  useEffect(() => {
    if (selectedProgram && programId !== previousProgramId) {
      setFieldValues({ tuition: String(selectedProgram.tuition) });
      setPreviousProgramId(programId);
    }
  }, [selectedProgram, programId, previousProgramId, setFieldValues]);

  const tuitionPeriodLabel = selectedProgram?.tuitionPeriod
    ? selectedProgram.tuitionPeriod.charAt(0).toUpperCase() + selectedProgram.tuitionPeriod.slice(1)
    : '';

  return (
    <>
      <Flex.Item basis={{ xs: '100%', sm: 'calc(33.333% - 10.667px)' }}>
        <Field
          as={TextInput}
          name="tuition"
          label="Tuition"
          placeholder="e.g. $10000"
          required="Tuition is required"
          disabled={isCreatingIntake}
        />
      </Flex.Item>
      <Flex.Item basis={{ xs: '100%', sm: 'calc(33.333% - 10.667px)' }}>
        <TextInput label="Tuition Period" value={tuitionPeriodLabel} onChange={() => {}} disabled />
      </Flex.Item>
    </>
  );
}

export function IntakeCreateForm({ initialProps }: IntakeCreateFormProps) {
  const navigate = useNavigate();
  const toast = useToasts();
  const { Form, Field, useFieldValues, useSetFieldValues } = intakeCreateForm;
  const { createIntake, isCreatingIntake } = useCreateIntake();

  const { programs, isLoadingPrograms } = usePrograms({
    'page[size]': 1000,
    'page[number]': 1,
  });

  const programsMap = useMemo(() => {
    return programs.reduce(
      (acc, program) => {
        acc[program.id] = program;
        return acc;
      },
      {} as Record<string, (typeof programs)[0]>
    );
  }, [programs]);

  const campusOptions = Object.entries(initialProps.referenceData.campuses).map(([id, campus]) => ({
    value: id,
    label: campus.name,
  }));

  const intakeTermOptions = Object.entries(initialProps.referenceData.intakeTerms).map(([id, term]) => ({
    value: id,
    label: term.name,
  }));

  const programOptions = programs.map((program) => ({
    value: program.id,
    label: `${program.name} (${program.code})`,
  }));

  const handleSubmit = async (values: IntakeCreateFormFields) => {
    const dateError = validateDates(values);
    if (dateError) {
      toast.negative(new Error(dateError));
      return;
    }

    const { fee: applicationFee, error: feeError } = validateApplicationFee(values.applicationFee);
    if (feeError) {
      toast.negative(new Error(feeError));
      return;
    }

    const { tuition, error: tuitionError } = validateTuition(values.tuition);
    if (tuitionError) {
      toast.negative(new Error(tuitionError));
      return;
    }

    try {
      const toDateOnly = (value: string) => (value ? new Date(value).toISOString().slice(0, 10) : '');
      const toDateTime = (value: string | undefined) => (value ? new Date(value).toISOString() : undefined);

      await createIntake({
        programId: values.programId,
        campusId: values.campusId,
        intakeTermId: values.intakeTermId,
        firstDayOfClassOn: toDateOnly(values.firstDayOfClass),
        lastDayOfClassOn: toDateOnly(values.lastDayOfClass),
        applicationFee: applicationFee,
        acceptingAppsStartsAt: toDateTime(values.acceptingAppsStartAt),
        acceptingAppsClosesAt: toDateTime(values.acceptingAppsCloseAt),
        deferralDeadlineAt: toDateTime(values.deferralDeadlineAt),
        refundDeadlineAt: toDateTime(values.refundDeadlineAt),
        tuition: tuition,
      });

      toast.positive('Intake created successfully');
      navigate('/intake-management');
    } catch (error) {
      toast.negative(new Error(getErrorMessage(error)));
    }
  };

  const formDefaultValues: IntakeCreateFormFields = useMemo(
    () => ({
      programId: '',
      campusId: '',
      intakeTermId: '',
      firstDayOfClass: '',
      lastDayOfClass: '',
      applicationFee: '',
      tuition: '',
      acceptingAppsStartAt: '',
      acceptingAppsCloseAt: '',
      deferralDeadlineAt: '',
      refundDeadlineAt: '',
    }),
    []
  );

  return (
    <FormWrapper>
      <Form id="intake-create-form" defaultValues={formDefaultValues} onSubmit={handleSubmit}>
        <Flex direction="column" gap={4}>
          <Flex gap={4} direction={{ xs: 'column', sm: 'row' }} wrap>
            <Flex.Item basis={{ xs: '100%', sm: 'calc(33.333% - 10.667px)' }}>
              <Field
                as={Select}
                name="programId"
                label="Program Name"
                appearance="styled"
                required="Program is required"
                disabled={isLoadingPrograms || isCreatingIntake}
              >
                {programOptions.map((option) => (
                  <Select.Option key={option.value} value={option.value} label={option.label} />
                ))}
              </Field>
            </Flex.Item>
            <Flex.Item basis={{ xs: '100%', sm: 'calc(33.333% - 10.667px)' }}>
              <Field
                as={Select}
                name="campusId"
                label="Campus Name"
                appearance="styled"
                required="Campus is required"
                disabled={isCreatingIntake}
              >
                {campusOptions.map((option) => (
                  <Select.Option key={option.value} value={option.value} label={option.label} />
                ))}
              </Field>
            </Flex.Item>
            <Flex.Item basis={{ xs: '100%', sm: 'calc(33.333% - 10.667px)' }}>
              <Field
                as={Select}
                name="intakeTermId"
                label="Intake Term"
                appearance="styled"
                required="Intake Term is required"
                disabled={isCreatingIntake}
              >
                {intakeTermOptions.map((option) => (
                  <Select.Option key={option.value} value={option.value} label={option.label} />
                ))}
              </Field>
            </Flex.Item>
          </Flex>

          <Flex gap={4} direction={{ xs: 'column', sm: 'row' }} wrap>
            <Flex.Item basis={{ xs: '100%', sm: 'calc(33.333% - 10.667px)' }}>
              <Field
                as={DatePicker}
                name="firstDayOfClass"
                label="First Day of Class"
                granularity="day"
                minDate={minDate}
                maxDate={maxDate}
                required="First Day of Class is required"
                disabled={isCreatingIntake}
              />
            </Flex.Item>
            <Flex.Item basis={{ xs: '100%', sm: 'calc(33.333% - 10.667px)' }}>
              <Field
                as={DatePicker}
                name="lastDayOfClass"
                label="Last Day of Class"
                granularity="day"
                minDate={minDate}
                maxDate={maxDate}
                required="Last Day of Class is required"
                disabled={isCreatingIntake}
              />
            </Flex.Item>
            <Flex.Item basis={{ xs: '100%', sm: 'calc(33.333% - 10.667px)' }}>
              <Field
                as={TextInput}
                name="applicationFee"
                label="Application Fee"
                placeholder="e.g. $1500"
                required="Application Fee is required"
                disabled={isCreatingIntake}
              />
            </Flex.Item>
          </Flex>

          <Flex gap={4} direction={{ xs: 'column', sm: 'row' }} wrap>
            <TuitionFields
              programsMap={programsMap}
              useFieldValues={useFieldValues}
              useSetFieldValues={useSetFieldValues}
              Field={Field}
              isCreatingIntake={isCreatingIntake}
            />
          </Flex>

          <Flex gap={4} direction={{ xs: 'column', sm: 'row' }} wrap>
            <Flex.Item basis={{ xs: '100%', sm: 'calc(33.333% - 10.667px)' }}>
              <Field
                as={DatePicker}
                name="acceptingAppsStartAt"
                label="Accepting Application Starts At"
                granularity="day"
                minDate={minDate}
                maxDate={maxDate}
                required="Accepting Applications Start is required"
                disabled={isCreatingIntake}
              />
            </Flex.Item>
            <Flex.Item basis={{ xs: '100%', sm: 'calc(33.333% - 10.667px)' }}>
              <Field
                as={DatePicker}
                name="acceptingAppsCloseAt"
                label="Accepting Application Closes At"
                granularity="day"
                minDate={minDate}
                maxDate={maxDate}
                required="Accepting Applications Close is required"
                disabled={isCreatingIntake}
              />
            </Flex.Item>
          </Flex>

          <Flex gap={4} direction={{ xs: 'column', sm: 'row' }} wrap>
            <Flex.Item basis={{ xs: '100%', sm: 'calc(33.333% - 10.667px)' }}>
              <Field
                as={DatePicker}
                name="deferralDeadlineAt"
                label="Deferral Deadline At"
                granularity="day"
                minDate={minDate}
                maxDate={maxDate}
                disabled={isCreatingIntake}
              />
            </Flex.Item>
            <Flex.Item basis={{ xs: '100%', sm: 'calc(33.333% - 10.667px)' }}>
              <Field
                as={DatePicker}
                name="refundDeadlineAt"
                label="Refund Deadline At"
                granularity="day"
                minDate={minDate}
                maxDate={maxDate}
                disabled={isCreatingIntake}
              />
            </Flex.Item>
          </Flex>
        </Flex>
      </Form>
    </FormWrapper>
  );
}

const FormWrapper = styled.div`
  width: 100%;
  max-width: 1200px;
`;
